import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';

const STORAGE_KEY = 'nova-theme';

const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {}, isMarketing: false });

export const useTheme = () => useContext(ThemeContext);

// Theme classification (architectural fix):
// Marketing/static pages are dark-capable; the authenticated app is always light.
// We use an explicit ALLOWLIST of marketing routes. Anything NOT listed is treated
// as an APP route and stays light. This makes the safe failure mode the default:
// a brand-new app route can never accidentally inherit the dark `.dark` tokens
// (which previously turned shadcn `bg-card` navy while hardcoded text stayed dark).
const MARKETING_PREFIXES = [
  '/about', '/features', '/pricing', '/security', '/trust',
  '/contact', '/dpa', '/privacy-policy', '/terms-of-service',
  '/login', '/register',
];

export const isMarketingPath = (pathname) => {
  if (pathname === '/') return true; // home/landing
  // NOTE: '/t/:slug' (public customer Trust Center) is light-first by design and is
  // intentionally NOT marketing — it must never receive the `.dark` class.
  return MARKETING_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  const setTheme = (next) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const marketing = isMarketingPath(location.pathname);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark' && marketing) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, marketing]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isMarketing: marketing }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeToggle = ({ className = '' }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle-button"
      className={`inline-flex items-center justify-center rounded-full p-2 text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/10 transition-colors ${className}`}
    >
      {isDark ? (
        <Sun className="w-[18px] h-[18px]" strokeWidth={1.5} />
      ) : (
        <Moon className="w-[18px] h-[18px]" strokeWidth={1.5} />
      )}
    </button>
  );
};
